import React, { useState, useCallback } from "react";
import { Input, Button } from "@fluentui/react-components";
import PropTypes from "prop-types";
import { FluentField } from "./Fluent-Field";
import FluentUiIcon from "../../assets/fluent-ui-icon";
import { URL_SCHEMES } from "../../util/general-helpers";
import { 
  validateUrl, 
  stripProtocol, 
  normalizeUrl,
  getIconForProtocol
} from "./utils/Fluent-Input-Url.util";
import "./fluent-overide.scss";

export const FluentInputUrl = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  protocol = "https://",
  name,
  onBlur,
  id,
  size = "medium",
  placeholder,
  validationMessage,
}) => {
  const [touched, setTouched] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const displayValue = stripProtocol(value);
  const protocolIcon = getIconForProtocol(protocol);

  const validationResult = touched 
    ? validateUrl(value, required, protocol) 
    : { valid: true, error: "" };
  
  const internalError = validationResult.error;
  const displayError = validationMessage || internalError;

  const fullUrl = value ? normalizeUrl(stripProtocol(value), protocol) : "";
  const { valid: isValidUrl } = validateUrl(fullUrl, false, protocol);
  const showOpenButton = isValidUrl && fullUrl?.trim() && fullUrl !== protocol;

  const handleChange = useCallback((event, data) => {
    const rawValue = data.value || "";
    
    if (!rawValue) {
      onChange?.(event, {
        ...data,
        name,
        value: "",
      });
      return;
    }
    
    const isTypingProtocol = URL_SCHEMES.some(scheme => 
      scheme.toLowerCase().startsWith(rawValue.toLowerCase())
    );
    
    const hasCompleteProtocol = URL_SCHEMES.some(scheme =>
      rawValue.toLowerCase().startsWith(scheme.toLowerCase())
    );
    
    const newValue = (isTypingProtocol || hasCompleteProtocol)
      ? rawValue
      : `${protocol}${rawValue}`;

    onChange?.(event, {
      ...data,
      name,
      value: newValue,
    });
  }, [name, onChange, protocol]);

  const handleBlur = useCallback((event) => {
    setTouched(true);

    if (value) {
      const strippedValue = stripProtocol(value);
      const normalizedValue = normalizeUrl(strippedValue, protocol);

      if (normalizedValue !== value) {
        onChange?.(event, {
          name,
          value: normalizedValue,
        });
      }
    }
    
    onBlur?.(event);
  }, [value, protocol, name, onChange, onBlur]);

  const handleOpenUrl = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!showOpenButton || !fullUrl) {
      return;
    }
    
    try {
      const isSpecialProtocol = fullUrl.startsWith('mailto:') || fullUrl.startsWith('tel:');
      const urlToOpen = isSpecialProtocol ? fullUrl : new URL(fullUrl).href;
      
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to open URL:", fullUrl, err);
    }
  }, [showOpenButton, fullUrl]);

  const handleCopyUrl = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const textToCopy = fullUrl || protocol;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  }, [fullUrl, protocol]);

  return (
    <FluentField
      label={label}
      required={required}
      validationMessage={displayError}
      size={size}
    >
      <Input
        id={id}
        name={name}
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        type="text"
        onChange={handleChange}
        onBlur={handleBlur}
        className="fluentUrlInput"
        contentBefore={
          <div className={`url-input-prefix ${disabled ? 'disabled' : ''}`}>
            <FluentUiIcon 
              name={protocolIcon} 
              size={18} 
              disabled={disabled} 
            />
            <span className={`protocol-text ${disabled ? 'disabled' : ''}`}>
              {protocol}
            </span>
          </div>
        }
        contentAfter={
          <div className="url-input-actions">
            <Button
              appearance="subtle"
              size="small"
              icon={
                <FluentUiIcon
                  name={copySuccess ? "CheckmarkRegular" : "CopyRegular"}
                  size={18}
                  color={copySuccess ? "#107C10" : "#424242"}
                />
              }
              onClick={handleCopyUrl}
              aria-label={copySuccess ? "Copied!" : "Copy URL"}
              title={copySuccess ? "Copied!" : "Copy URL"}
            />
            <Button
              appearance="subtle"
              size="small"
              icon={
                <FluentUiIcon
                  name="OpenBundle"
                  size={18}
                  disabled={!showOpenButton}
                  color={showOpenButton ? '#0F6CBD' : '#A19F9D'}
                />
              }
              disabled={!showOpenButton}
              onClick={handleOpenUrl}
              aria-label="Open URL in new tab"
              title="Open URL in new tab"
            />
          </div>
        }
      />
    </FluentField>
  );
};

FluentInputUrl.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  protocol: PropTypes.oneOf(URL_SCHEMES),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  id: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  placeholder: PropTypes.string,
  validationMessage: PropTypes.string,
};